type AnyFn = (...args: never[]) => unknown;

export function debounce<T extends AnyFn>(
  callback: T,
  wait: number,
  context?: ThisParameterType<T> | unknown
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  let callbackArgs: Parameters<T> | undefined;
  const ctx = context;

  const later = () => {
    if (callbackArgs) {
      callback.apply(ctx as ThisParameterType<T>, callbackArgs);
    }
  };

  return function debounced(this: unknown, ...args: Parameters<T>) {
    callbackArgs = args;
    if (timeout !== undefined) clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

type Waiter<T> = {
  resolve: (value: T) => void;
  reject: (reason?: unknown) => void;
};

export function debouncePromise<T extends AnyFn>(
  fn: T,
  wait = 0
): (...args: Parameters<T>) => Promise<Awaited<ReturnType<T>>> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  let waiters: Waiter<Awaited<ReturnType<T>>>[] = [];

  return function debounced(this: unknown, ...args: Parameters<T>) {
    return new Promise<Awaited<ReturnType<T>>>((resolve, reject) => {
      waiters.push({ resolve, reject });

      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }

      timeoutId = setTimeout(() => {
        const currentWaiters = waiters;
        waiters = [];
        timeoutId = undefined;

        Promise.resolve(fn.apply(this, args) as Awaited<ReturnType<T>>).then(
          (result) => {
            currentWaiters.forEach((waiter) => waiter.resolve(result));
          },
          (error: unknown) => {
            currentWaiters.forEach((waiter) => waiter.reject(error));
          }
        );
      }, wait);
    });
  };
}

type PendingEntry<TMeta, TResult> = {
  timer?: ReturnType<typeof setTimeout>;
  waiters: Waiter<TResult>[];
  meta: TMeta;
};

/** Cart / filter keys may be numeric ids — normalize for Map lookup. */
export type DebounceKey = string | number;

function normalizeKey(key: DebounceKey): string {
  return String(key);
}

/**
 * Per-key debounced async runner.
 * - Coalesces callers into one shared Promise (no hanging waiters)
 * - Exposes sequence tokens so stale in-flight results can be ignored
 */
export function createKeyedAsyncDebouncer(wait = 0) {
  const pending = new Map<string, PendingEntry<unknown, unknown>>();
  const seqByKey = new Map<string, number>();

  function bumpSeq(key: DebounceKey): number {
    const k = normalizeKey(key);
    const seq = (seqByKey.get(k) || 0) + 1;
    seqByKey.set(k, seq);
    return seq;
  }

  function currentSeq(key: DebounceKey): number {
    return seqByKey.get(normalizeKey(key)) || 0;
  }

  function isCurrentSeq(key: DebounceKey, seq: number): boolean {
    return currentSeq(key) === seq;
  }

  function takePending(key: DebounceKey): PendingEntry<unknown, unknown> | null {
    const k = normalizeKey(key);
    const entry = pending.get(k);
    if (!entry) return null;
    if (entry.timer) clearTimeout(entry.timer);
    pending.delete(k);
    return entry;
  }

  function resolveWaiters<T>(waiters: Waiter<T>[], result: T): void {
    waiters.forEach(({ resolve }) => resolve(result));
  }

  function rejectWaiters(waiters: Waiter<unknown>[], error: unknown): void {
    waiters.forEach(({ reject }) => reject(error));
  }

  function schedule<TMeta, TResult>(
    key: DebounceKey,
    meta: TMeta,
    run: (seq: number, meta: TMeta) => TResult | Promise<TResult>
  ): Promise<TResult> {
    const k = normalizeKey(key);
    return new Promise((resolve, reject) => {
      const existing = pending.get(k) as PendingEntry<TMeta, TResult> | undefined;
      if (existing?.timer) clearTimeout(existing.timer);

      const waiters = existing ? existing.waiters : [];
      waiters.push({ resolve, reject });

      const timer = setTimeout(() => {
        pending.delete(k);
        const seq = currentSeq(k);
        Promise.resolve(run(seq, meta)).then(
          (result) => resolveWaiters(waiters, result),
          (error: unknown) => rejectWaiters(waiters as Waiter<unknown>[], error)
        );
      }, wait);

      pending.set(k, { timer, waiters, meta } as PendingEntry<unknown, unknown>);
    });
  }

  function runImmediate<TMeta, TResult>(
    key: DebounceKey,
    meta: TMeta,
    run: (seq: number, meta: TMeta) => TResult | Promise<TResult>
  ): Promise<TResult> {
    const k = normalizeKey(key);
    const entry = takePending(k) as PendingEntry<TMeta, TResult> | null;
    const waiters = entry ? entry.waiters : [];
    const seq = currentSeq(k);
    const resultPromise = Promise.resolve(run(seq, meta));

    if (waiters.length) {
      resultPromise.then(
        (result) => resolveWaiters(waiters, result),
        (error: unknown) => rejectWaiters(waiters as Waiter<unknown>[], error)
      );
    }

    return resultPromise;
  }

  function cancel(key: DebounceKey, result: unknown = null): void {
    bumpSeq(key);
    const entry = takePending(key);
    if (entry) resolveWaiters(entry.waiters, result);
  }

  function cancelAll(result: unknown = null): void {
    for (const key of pending.keys()) {
      cancel(key, result);
    }
    seqByKey.clear();
  }

  function getMeta(key: DebounceKey): unknown {
    return pending.get(normalizeKey(key))?.meta;
  }

  return {
    schedule,
    runImmediate,
    cancel,
    cancelAll,
    bumpSeq,
    currentSeq,
    isCurrentSeq,
    getMeta
  };
}

export default debounce;
